import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { supabase } from '../config/supabase';
import { useAuth } from '../context/AuthContext';
import { useTenantSlug } from '../context/TenantSlugContext';
import { buildTenantPath } from '../utils/tenantSlug';

interface NewsItem {
  id: string;
  title: string;
  excerpt: string;
  image: string;
  publishDate: string; // Tipo String para Supabase
}

const NewsPage = () => {
  const [newsList, setNewsList] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { selectedClient, isLoading: authLoading } = useAuth();
  const { tenantSlug } = useTenantSlug();
  const effectiveSlug = tenantSlug || selectedClient?.clientSlug || null;

  useEffect(() => {
    const fetchNews = async () => {
      if (authLoading) return;

      if (!effectiveSlug) {
        setNewsList([]);
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .rpc("get_public_news_by_slug", { p_slug: effectiveSlug });

        if (error) {
          throw error;
        }

        const newsData = ((data || []) as any[]).map((item) => ({
          id: item.newspageId,
          title: item.newspageTitle,
          excerpt: item.newspageExcerpt,
          image: item.newspageImage,
          publishDate: item.newspagePublishDate,
        })) as NewsItem[];

        setNewsList(newsData);
      } catch (err) {
        console.error("Erro ao buscar notícias:", err);
        setNewsList([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [authLoading, effectiveSlug]);

  if (authLoading || loading) {
    return <p className="text-center py-8">Carregando notícias...</p>;
  }

  if (!effectiveSlug) {
    return <p className="text-center py-8">Selecione uma clínica para visualizar notícias.</p>;
  }

  if (newsList.length === 0) {
    return <p className="text-center py-8">Nenhuma notícia publicada ainda.</p>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <Link to={buildTenantPath("/", tenantSlug)} className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600 mb-8">
          <ArrowLeft size={16} className="mr-1" />
          Voltar para o início
        </Link>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {newsList.map(news => (
            <Link
              key={news.id}
              to={buildTenantPath(`/noticias/${news.id}`, tenantSlug)}
              className="block bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="aspect-w-16 aspect-h-9 overflow-hidden">
                <img
                  src={news.image}
                  alt={news.title}
                  className="w-full h-full object-cover object-center transition-transform hover:scale-105"
                />
              </div>
              <div className="p-4">
                <h2 className="text-xl font-semibold mb-2 line-clamp-2">{news.title}</h2>
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">{news.excerpt}</p>
                <p className="text-gray-500 text-xs">
                  {new Date(news.publishDate).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewsPage;
